import axios from 'axios'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'
import { config } from '../../function/config'
import AdminHeader from './AdminHeader'

export default function AddDepartment() {
  const navigate = useNavigate()
  const token = localStorage.getItem('token')

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  const [selectedFile, setSelectedFile] = useState(null)
  const [openUploadModal, setOpenUploadModal] = useState(false)

  const [formData, setFormData] = useState({
    department_name: '',
    description: '',
  })

  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }))
  }

  // Validation
  const validateForm = (data) => {
    const newErrors = {}

    // Branch Name
    if (!data.department_name.trim()) {
      newErrors.department_name = 'Department Name is required'
    }

    // Address
    if (!data.description.trim()) {
      newErrors.description = 'Description is required'
    }

    return newErrors
  }

  // Submit Single Branch
const handleSubmit = async (e) => {
  e.preventDefault()

  const validationErrors = validateForm(formData)
  setErrors(validationErrors)

  if (Object.keys(validationErrors).length > 0) {
    const firstError = Object.values(validationErrors)[0]
    toast.error(firstError)
    return
  }

  try {
    setLoading(true)

    // ============================================================
    // GET EXISTING DEPARTMENTS
    // ============================================================

    const existingResponse = await axios.get(
      `${config.baseurl}get_all_departments/`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        }
      }
    )

    const existingDepartments =
      existingResponse.data?.data || []

    // ============================================================
    // NORMALIZE DEPARTMENT NAME
    // ============================================================

    const departmentName = formData.department_name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')

    // ============================================================
    // CHECK DUPLICATE DEPARTMENT NAME
    // ============================================================

    const departmentExists = existingDepartments.some(
      (department) => {

        const existingName = String(
          department.department_name || ''
        )
          .trim()
          .toLowerCase()
          .replace(/\s+/g, ' ')

        return existingName === departmentName
      }
    )

    if (departmentExists) {
      toast.error('Department name already exists')
      return
    }

    // ============================================================
    // CREATE DEPARTMENT
    // ============================================================

    const payload = {
      department_name: formData.department_name.trim(),
      description: formData.description.trim(),
    }

    const res = await axios.post(
      `${config.baseurl}create_department/`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        }
      }
    )

    if (res.status === 200 || res.status === 201) {

      toast.success(
        res.data?.message ||
        'Department created successfully'
      )

      setFormData({
        department_name: '',
        description: '',
      })

      setTimeout(() => {
        navigate(-1)
      }, 300)

    } else {
      toast.error(
        res.data?.message ||
        'Failed to create department'
      )
    }
    } catch (error) {
        console.error('Error:', error.response?.data || error)

        const errorData = error.response?.data

        if (errorData?.message && typeof errorData.message === 'object') {

            Object.entries(errorData.message).forEach(([field, messages]) => {

                if (Array.isArray(messages)) {
                    messages.forEach((msg) => {
                        toast.error(msg)
                    })
                } else {
                    toast.error(messages)
                }

            })

        } else if (errorData?.message) {

            toast.error(errorData.message)

        } else if (errorData?.error) {

            toast.error(errorData.error)

        } else if (errorData?.detail) {

            toast.error(errorData.detail)

        } else {

            toast.error('Failed to create staff')
        }

    } finally {
        setLoading(false)
    }
  }

  // Reusable Input Class
  const inputClass ='w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 placeholder:text-slate-400'

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 ml-64 5xl:ml-0">
      <AdminHeader />

      {/* HEADER */}
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-6">
        <div className="items-center justify-between text-center">
          <h1 className="mt-1 text-3xl font-bold text-slate-800">
            ADD DEPARTMENTS
          </h1>

          <p className="mt-2 text-sm text-slate-500">
            Create a new department or add multiple departments using an Excel file.
          </p>
        </div>
      </div>

      {/* FORM SECTION */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <div className="mx-auto max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">

          {/* Form Header */}
          <div className="border-b border-slate-200 bg-slate-50 px-6 py-5 text-center text-lg">
            <h2 className="text-lg font-bold text-slate-800">
              Department Registration Form
            </h2>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 md:p-8">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">

              {/* department Name */}
              <div className="md:col-span-2">
                <label
                  htmlFor="department_name"
                  className="mb-2 block text-sm font-semibold text-slate-700"
                >
                  Department Name
                  <span className="ml-1 text-red-500">
                    *
                  </span>
                </label>

                <input
                  id="department_name"
                  type="text"
                  name="department_name"
                  placeholder="Enter department name"
                  value={formData.department_name}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label
                  htmlFor="description"
                  className="mb-2 block text-sm font-semibold text-slate-700"
                >
                  Departments
                  <span className="ml-1 text-red-500">
                    *
                  </span>
                </label>

                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  placeholder="Enter complete branch description"
                  value={formData.description}
                  onChange={handleChange}
                  className={`${inputClass} resize-none`}
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-200 pt-6 sm:flex-row justify-end">
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={loading}
                className="rounded-xl bg-slate-100 px-6 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading}
                className="rounded-xl bg-[#000435] px-7 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#000326] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

